import { Mail, MapPin, Timer } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BackLink } from "@/components/BackLink";
import { FinalCtaSection, H_SECTION, SUBLINE } from "@/components/marketing/Sections";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useJsonLd, usePageMeta } from "@/lib/seo";
import { BRAND } from "@/config/brand";

const FACTS = [
  { icon: Mail, key: "channel" },
  { icon: Timer, key: "response" },
  { icon: MapPin, key: "coverage" },
];

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [tsToken, setTsToken] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  usePageMeta({ title: `${t("contact.title")} — ${BRAND.name}`, description: t("contact.subtitle"), localized: true });
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: t("contact.title"),
    description: t("contact.subtitle"),
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/contact", { ...form, turnstile_token: tsToken });
      setSent(true);
      toast(t("contact.sentToast"));
    } catch {
      toast.error(t("common.error"));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "rounded-xl bg-white h-11";

  return (
    <div data-testid="contact-page">
      <section className="bg-white pt-10">
        <div className="max-w-[980px] mx-auto px-6">
          <BackLink fallback="/" testid="contact-back-link" />
        </div>
        <div className="max-w-[980px] mx-auto px-6 pt-10 lg:pt-14 text-center flex flex-col items-center gap-5">
          <h1 className={`${H_SECTION} text-[#1d1d1f]`}>{t("contact.title")}</h1>
          <p className={`${SUBLINE} text-[#6e6e73] max-w-2xl`}>{t("contact.subtitle")}</p>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-[980px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-14 items-start">
          {sent ? (
            <div className="bg-[#f5f5f7] rounded-3xl p-10 lg:p-14 text-center flex flex-col items-center gap-4" data-testid="contact-success">
              <span className="w-12 h-12 rounded-full bg-[#1D4D3B]/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#1D4D3B]" aria-hidden="true" />
              </span>
              <h2 className="text-2xl font-semibold tracking-tight text-[#1d1d1f]">{t("contact.sentTitle")}</h2>
              <p className="text-sm text-[#6e6e73] max-w-sm leading-relaxed">{t("contact.sentBody")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-[#f5f5f7] rounded-3xl p-8 lg:p-12 flex flex-col gap-5" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ct-name">{t("contact.name")}</Label>
                  <Input id="ct-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} autoComplete="name" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ct-email">{t("contact.email")}</Label>
                  <Input id="ct-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} autoComplete="email" />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ct-subject">{t("contact.subject")}</Label>
                <Input id="ct-subject" data-testid="contact-subject-input" value={form.subject} onChange={set("subject")} className={inputCls} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ct-message">{t("contact.message")}</Label>
                <Textarea id="ct-message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")} className="rounded-xl bg-white resize-none" />
              </div>
              <TurnstileWidget onToken={setTsToken} />
              <Button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="self-start rounded-full h-11 px-8 bg-[#0A0A0A] text-white hover:bg-black/80 transition-colors duration-300"
              >
                {sending ? "…" : t("contact.send")}
              </Button>
            </form>
          )}

          <aside className="flex flex-col gap-8 lg:pt-4">
            {FACTS.map(({ icon: Icon, key }) => (
              <div key={key} className="flex flex-col gap-2">
                <Icon className="w-5 h-5 text-[#1d1d1f]" strokeWidth={1.5} aria-hidden="true" />
                <h3 className="text-[15px] font-semibold text-[#1d1d1f]">{t(`contact.${key}Title`)}</h3>
                <p className="text-sm text-[#6e6e73] leading-relaxed">{t(`contact.${key}Desc`)}</p>
              </div>
            ))}
          </aside>
        </div>
      </section>

      <FinalCtaSection />
    </div>
  );
}
